import { useCallback } from 'react';
import { useApi, useCaller } from '@/hooks/api.hook';
import { idempotencyConfig } from '@/utils/idempotency';

export const useAuthService = () => {
  const api = useApi('');
  const caller = useCaller();
  const options = { loadingKey: 'identity-auth', returnErrorResponse: true } as const;
  const config = { skipAuth: true };

  const login = useCallback(
    (identifier: string, password: string) => caller<ApiResult<AuthLoginResult>>(
      () => api.post('/v1/identity/auth/login', { identifier, password }, config),
      options,
    ),
    [api, caller],
  );

  const verifyLogin = useCallback(
    (identifier: string, code: string, challengeId: string) => caller<ApiResult<AuthLoginResult['tokens']>>(
      () => api.post('/v1/identity/auth/login/verify', { identifier, code, challengeId }, config),
      options,
    ),
    [api, caller],
  );

  const register = useCallback(
    (body: AuthRegisterRequest) =>
      caller<ApiResult<{ userId: string; email: string; verification: import('../utils/verification').Verification | null }>>(
        () => api.post('/v1/identity/auth/register', body, config),
        options,
      ),
    [api, caller],
  );

  const verifyEmail = useCallback(
    (destination: string, code: string, challengeId?: string) => caller<ApiResult<Record<string, never>>>(
      () => api.post('/v1/identity/tokens/verify-email', { destination, code, challengeId }, config),
      options,
    ),
    [api, caller],
  );

  const requestEmailVerification = useCallback(
    (destination: string, challengeId?: string) => caller<ApiResult<{ verification: import('../utils/verification').Verification | null }>>(
      () => api.post('/v1/identity/tokens/request', {
        purpose: 'email_verification',
        challengeId,
        channel: 'email',
        destination,
      }, config),
      options,
    ),
    [api, caller],
  );

  const changePassword = useCallback(
    (currentPassword: string, newPassword: string) => caller<ApiResult<null>>(
      () => api.post(
        '/v1/identity/me/password',
        { currentPassword, newPassword },
        idempotencyConfig(),
      ),
      {
        loadingKey: 'change-password',
        successMessage: 'Password changed successfully.',
        errorMessage: 'Unable to change password.',
        returnErrorResponse: true,
      },
    ),
    [api, caller],
  );

  return { login, verifyLogin, register, verifyEmail, requestEmailVerification, changePassword };
};
